import { faMagnifyingGlass, faPlus, faTimes, faSort, faSortUp, faSortDown, faSpinner } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { useNavigate, useLocation } from 'react-router-dom'
import { useOhData } from '../../../layouts/OhLayout'
import { useState, useEffect } from 'react'

import useAxiosPrivate from '../../../hooks/useAxiosPrivate'
import useAuth from '../../../hooks/useAuth'
import moment from 'moment'

import CancelRequest from '../../../components/CancelRequest'
import OHViewRequest from '../../../components/OHViewRequest'
import SlideOut from '../../../components/SlideOut'

const OHDashboard = ({ previousPolicy, orgInfo, availableSchools, counts }) => {
    const axios = useAxiosPrivate()
    const navigate = useNavigate()
    const location = useLocation()

    const { setOrgInfo, setRequestData } = useOhData()
    const { auth, setShowLoader, createInfo } = useAuth()

    const [recordViewType, setRecordViewType] = useState('current')
    const [cancelModalOpen, setCancelModalOpen] = useState(false)
    const [selectedRequest, setSelectedRequest] = useState(null)
    const [selectedSchool, setSelectedSchool] = useState('')
    const [policyView, setPolicyView] = useState((moment(auth?.currentPolicy?.policy_expire)?.isBefore(moment()) ?? null) ? 'previous' : 'current')
    const [loading, setLoading] = useState(false)
    const [open, setOpen] = useState(false)

    const [sortDirection, setSortDirection] = useState('desc')
    const [sortField, setSortField] = useState('created_at')
    const [itemsPerPage, setItemsPerPage] = useState(10)
    const [currentPage, setCurrentPage] = useState(1)
    const [searchTerm, setSearchTerm] = useState('')
    const [totalItems, setTotalItems] = useState(0)
    const [totalPages, setTotalPages] = useState(0)
    const [fromItem, setFromItem] = useState(0)
    const [toItem, setToItem] = useState(0)

    const [currentPolicyExpired, setCurrentPolicyExpired] = useState(moment(auth?.currentPolicy?.policy_expire)?.isBefore(moment()) ?? true)

    const [requests, setRequests] = useState([])

    useEffect(() => {
        if (auth?.currentPolicy) {
            fetchRequests()
        }
    }, [
        currentPage,
        itemsPerPage,
        sortField,
        sortDirection,
        selectedSchool,
        recordViewType,
        policyView,
        auth?.currentPolicy
    ])

    useEffect(() => {
        const expired = moment(auth?.currentPolicy?.policy_expire)?.isBefore(moment()) ?? null
        setCurrentPolicyExpired(expired ?? true)
        if (expired) {
            setPolicyView('previous')
        }
    }, [auth?.currentPolicy])
    
    useEffect(() => {
        setCurrentPage(1)
    }, [recordViewType, policyView, selectedSchool])

    useEffect(() => {
        const delayDebounceFn = setTimeout(() => {
            if (auth?.currentPolicy) {
                setCurrentPage(1)
                fetchRequests()
            }
        }, 500)

        return () => clearTimeout(delayDebounceFn)
    }, [searchTerm])

    useEffect(() => {
        const state = location.state
        if (state?.refreshData) {
            fetchRequests()
            window.history.replaceState({}, document.title)
        }
    }, [location.state])

    const handleSort = (field) => {
        if (sortField === field) {
            setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc')
        } else {
            setSortField(field)
            setSortDirection('asc')
        }
    }

    const fetchRequests = async () => {
        setLoading(true)
        try {
            const policyId = policyView === 'current'
                ? auth.currentPolicy.policy_id
                : previousPolicy?.policy_id

            if (!policyId) return []

            let viewAll = recordViewType === 'all' && auth.user.master_oha === 1

            const endpoint = viewAll
                ? `/oh/service-requests/all/${policyId}/${auth.user.contact_id}`
                : `/oh/service-requests/${policyId}/${auth.user.contact_id}`

            const response = await axios.post(endpoint, {
                searchTerm,
                sortField,
                sortDirection,
                perPage: itemsPerPage,
                page: currentPage,
                selectedSchool: viewAll ? 'all' : selectedSchool
            })

            if (response.status !== 200 || response.data.error) {
                createInfo('error', 'Error fetching OH requests')
                return []
            }

            const data = response.data.requests || []

            const processedData = data.map((item) => ({
                ...item,
                request_info_array: JSON.parse(item.request_information || '{}')
            }))

            if (!!selectedRequest) {
                const updatedRequest = processedData.find(req => req.id === selectedRequest.id)
                if (updatedRequest) {
                    setSelectedRequest(updatedRequest)
                }
            }

            setRequests(processedData)
            setTotalItems(response.data.pagination?.total || 0)
            setTotalPages(response.data.pagination?.last_page || 0)
            setFromItem(response.data.pagination?.from || 0)
            setToItem(response.data.pagination?.to || 0)

            return processedData
        } catch (error) {
            console.error('Error fetching requests:', error)
            createInfo('error', 'Error fetching OH requests')
            return []
        } finally {
            setLoading(false)
        }
    }

    const openRequest = () => {
        setOrgInfo(orgInfo)
        setRequestData(null)
        navigate('/oh/oha-request')
    }

    const handleViewRequest = (request) => {
        const isDraft = (request?.request_status || '').toLowerCase() === 'draft'

        if (isDraft) {
            setOrgInfo(orgInfo)
            setRequestData(request)
            navigate('/oh/oha-request')
        } else {
            setSelectedRequest(request)
            setOpen(true)
        }
    }

    const handleCancelRequest = (request) => {
        setSelectedRequest(request)
        setCancelModalOpen(true)
    }

    const closeModal = () => {
        setCancelModalOpen(false)
        setSelectedRequest(null)
    }

    const closeSlideOut = (refreshData = false) => {
        setOpen(false)
        setSelectedRequest(null)

        if (refreshData) {
            fetchRequests()
        }
    }

    const cancelRequestClicked = async () => {
        setCancelModalOpen(false)
        setShowLoader(true)
        try {
            const isDraft = (selectedRequest?.request_status || '').toLowerCase() === 'draft'
            const endpoint = isDraft
                ? `/service-requests/oh/cancel-draft/${selectedRequest.id}`
                : `/service-requests/oh/cancel/${selectedRequest.id}`

            const response = await axios.post(endpoint)
            if (response.status === 200) {
                createInfo('success', isDraft ? 'Draft cancelled successfully' : 'Request cancelled successfully')
                fetchRequests()
            }
        } catch (error) {
            console.error('Error cancelling request:', error)
            createInfo('error', 'Error processing request')
        }
        setShowLoader(false)
    }

    const clearFilter = () => {
        setSelectedSchool('')
        setSearchTerm('')
        setCurrentPage(1)
    }

    const nextPage = () => {
        if (currentPage < totalPages) {
            setCurrentPage(currentPage + 1)
        }
    }

    const previousPage = () => {
        if (currentPage > 1) {
            setCurrentPage(currentPage - 1)
        }
    }

    const statusClass = (status) => {
        if (typeof status !== 'string') {
            return 'bg-gray-200 text-gray-800'
        }

        switch (status.toLowerCase()) {
            case 'draft':
            case 'awaiting submission':
            case 'awaiting review':
            case 'awaiting confirmation of appointment':
                return 'bg-blue-200 text-blue-800 border-blue-400'
            case 'in progress':
            case 'awaiting consent':
            case 'awaiting assessment':
            case 'awaiting clinical review':
            case 'appointment confirmed':
            case 'appointment attended':
            case 'awaiting client review':
                return 'bg-amber-200 text-amber-800 border-amber-400'
            case 'consent declined':
            case 'referral cancelled':
            case 'cancelled':
            case 'withdrawn consent':
                return 'bg-red-200 text-red-800 border-red-400'
            case 'completed':
            case 'report ready':
            case 'closed':
                return 'bg-green-200 text-green-800 border-green-400'
            case 'dna/unable to proceed':
                return 'bg-gray-200 text-gray-800 border-gray-400'
            default:
                return 'bg-gray-200 text-gray-800'
        }
    }

    return (
        <div>
            {previousPolicy && Object.keys(previousPolicy).length > 0 && (
                <div className="flex w-full">
                    { !currentPolicyExpired &&
                        <button
                            className={`w-1/8 py-2 px-2 mr-2 text-sm rounded-t-md text-center flex items-center font-bold ${policyView === 'current' ? 'bg-white' : 'bg-gray-200'}`}
                            onClick={() => setPolicyView('current')}
                        >
                            <p>Current Policy</p>
                            {loading && policyView === 'current' && (
                                <FontAwesomeIcon className="ml-2" icon={faSpinner} spin />
                            )}
                        </button>
                    }
                    <button
                        className={`w-1/8 py-2 px-2 mr-2 text-sm rounded-t-md text-center flex items-center font-bold ${policyView === 'previous' ? 'bg-white' : 'bg-gray-200'
                            }`}
                        onClick={() => setPolicyView('previous')}
                    >
                        <p>Previous Policy</p>
                        {loading && policyView === 'previous' && (
                            <FontAwesomeIcon className="ml-2" icon={faSpinner} spin />
                        )}
                    </button>
                </div>
            )}
            <div className="bg-white rounded-tr-md rounded-br-md rounded-bl-md p-4 w-full mx-auto shadow-md">
                <div className="pb-4 flex items-end justify-between ">
                    <div className="flex items-center space-x-2">
                        <div className="flex items-center bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-md h-11 ">
                            <div className="flex items-center pl-3 pointer-events-none text-gray-600">
                                <FontAwesomeIcon icon={faMagnifyingGlass} />
                            </div>
                            <input
                                type="text"
                                id="table-search"
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                className="bg-gray-50 w-80 pl-6"
                                placeholder="Search Requests"
                            />
                        </div>
                        {auth.user.master_oha === 1 && recordViewType === 'all' && (
                            <div className="flex items-center">
                                <div className="dropdown-container relative inline-block">
                                    <select
                                        defaultValue={selectedSchool}
                                        onChange={(e) => {
                                            setSelectedSchool(e.target.value)
                                        }}
                                        className="dropdown-filter border border-gray-300 rounded-md px-3 py-2 pr-10 focus:outline-none focus:ring-2 focus:ring-teal-600"
                                    >
                                        <option value="">All Schools</option>
                                        {(policyView === 'current' ? availableSchools?.current || [] : availableSchools?.previous || []).map((school, index) => (
                                            <option key={index} value={school}>
                                                {school}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                                {selectedSchool && (
                                    <div
                                        onClick={clearFilter}
                                        className="border flex items-center rounded h-11 border-red-700 cursor-pointe ml-2 cursor-pointer"
                                    >
                                        <div className="text-red-700 h-full flex justify-center items-center px-6 font-medium">
                                            Clear Filters
                                        </div>
                                        <div className="bg-red-600 text-white h-full flex w-[45px] justify-center items-center">
                                            <FontAwesomeIcon icon={faTimes} className="pl-2" />
                                        </div>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                    {policyView !== 'previous' && !currentPolicyExpired && (
                        <div className="mt-4 hover:shadow-md">
                            <button
                                type="submit"
                                className="button border-[#02ADAD]"
                                onClick={() => openRequest()}
                            >
                                <span className="text-[#02ADAD]">Submit Request</span>
                                <span className="bg-[#02ADAD]">
                                    <FontAwesomeIcon icon={faPlus} />
                                </span>
                            </button>
                        </div>
                    )}
                </div>
                <div className="overflow-hidden mt-4">
                    {auth.user.master_oha === 1 && (
                        <div className="flex items-center space-x-2 mb-4">
                            <button
                                onClick={() => setRecordViewType('current')}
                                className={`${recordViewType === 'current' ? 'bg-[#02ADAD]' : 'bg-gray-400'
                                    } hover:bg-teal-600 rounded-lg px-4 py-2 text-white cursor-pointer`}
                            >
                                Current OH (
                                {policyView === 'current'
                                    ? counts?.current || 0
                                    : counts?.previous?.current || 0}
                                )
                            </button>
                            <button
                                onClick={() => setRecordViewType('all')}
                                className={`${recordViewType === 'all' ? 'bg-[#02ADAD]' : 'bg-gray-400'
                                    } hover:bg-teal-600 rounded-lg px-4 py-2 text-white cursor-pointer`}
                            >
                                All OH Requests (
                                {policyView === 'current'
                                    ? counts?.all || 0
                                    : counts?.previous?.all || 0}
                                )
                            </button>
                        </div>
                    )}
                    <table className="table-main rounded-b-md rounded-tr-lg max-w-[1450px] w-full mx-auto">
                        <thead className="border-b font-medium dark:border-slate-500 bg-slate-100">
                            <tr className="bg-[#02ADAD]">
                                <th
                                    scope="col"
                                    className="cursor-pointer select-none"
                                    onClick={() => handleSort('reference')}
                                >
                                    <div className="flex justify-center items-center">
                                        <div className="mr-1">Reference</div>
                                        {sortField !== 'reference' ? (
                                            <FontAwesomeIcon icon={faSort} />
                                        ) : (
                                            <FontAwesomeIcon
                                                icon={
                                                    sortDirection === 'asc'
                                                        ? faSortUp
                                                        : sortDirection === 'desc'
                                                            ? faSortDown
                                                            : faSort
                                                }
                                            />
                                        )}
                                    </div>
                                </th>
                                <th
                                    scope="col"
                                    className="cursor-pointer select-none"
                                    onClick={() => handleSort('staff_name')}
                                >
                                    <div className="flex justify-center items-center">
                                        <div className="mr-1">Staff Member</div>
                                        {sortField !== 'staff_name' ? (
                                            <FontAwesomeIcon icon={faSort} />
                                        ) : (
                                            <FontAwesomeIcon
                                                icon={
                                                    sortDirection === 'asc'
                                                        ? faSortUp
                                                        : sortDirection === 'desc'
                                                            ? faSortDown
                                                            : faSort
                                                }
                                            />
                                        )}
                                    </div>
                                </th>
                                <th
                                    scope="col"
                                    className="cursor-pointer select-none"
                                    onClick={() => handleSort('date_of_birth')}
                                >
                                    <div className="flex justify-center items-center">
                                        <div className="mr-1">Date of Birth</div>
                                        {sortField !== 'date_of_birth' ? (
                                            <FontAwesomeIcon icon={faSort} />
                                        ) : (
                                            <FontAwesomeIcon
                                                icon={
                                                    sortDirection === 'asc'
                                                        ? faSortUp
                                                        : sortDirection === 'desc'
                                                            ? faSortDown
                                                            : faSort
                                                }
                                            />
                                        )}
                                    </div>
                                </th>
                                <th
                                    scope="col"
                                    className="cursor-pointer select-none"
                                    onClick={() => handleSort('status')}
                                >
                                    <div className="flex justify-center items-center">
                                        <div className="mr-1">Status</div>
                                        {sortField !== 'status' ? (
                                            <FontAwesomeIcon icon={faSort} />
                                        ) : (
                                            <FontAwesomeIcon
                                                icon={
                                                    sortDirection === 'asc'
                                                        ? faSortUp
                                                        : sortDirection === 'desc'
                                                            ? faSortDown
                                                            : faSort
                                                }
                                            />
                                        )}
                                    </div>
                                </th>
                                <th scope="col">Additional Info</th>
                                {recordViewType === 'all' && (
                                    <th
                                        scope="col"
                                        className="cursor-pointer select-none"
                                        onClick={() => handleSort('created_by')}
                                    >
                                        <div className="flex justify-center items-center">
                                            <div className="mr-1">Created By</div>
                                            {sortField !== 'created_by' ? (
                                                <FontAwesomeIcon icon={faSort} />
                                            ) : (
                                                <FontAwesomeIcon
                                                    icon={
                                                        sortDirection === 'asc'
                                                            ? faSortUp
                                                            : sortDirection === 'desc'
                                                                ? faSortDown
                                                                : faSort
                                                    }
                                                />
                                            )}
                                        </div>
                                    </th>
                                )}
                                <th
                                    scope="col"
                                    className="cursor-pointer select-none"
                                    onClick={() => handleSort('created_at')}
                                >
                                    <div className="flex justify-center items-center">
                                        <div className="mr-1">Created On</div>
                                        {sortField !== 'created_at' ? (
                                            <FontAwesomeIcon icon={faSort} />
                                        ) : (
                                            <FontAwesomeIcon
                                                icon={
                                                    sortDirection === 'asc'
                                                        ? faSortUp
                                                        : sortDirection === 'desc'
                                                            ? faSortDown
                                                            : faSort
                                                }
                                            />
                                        )}
                                    </div>
                                </th>
                                <th scope="col" className="py-4">
                                    Actions
                                </th>
                            </tr>
                        </thead>
                        <tbody>
                            {loading ? (
                                <tr>
                                    <td colSpan={recordViewType === 'all' ? 8 : 7} className="text-center py-8">
                                        <FontAwesomeIcon icon={faSpinner} spin className="mr-2" />
                                        Loading requests...
                                    </td>
                                </tr>
                            ) : requests.length > 0 ? (
                                requests.map((item, index) => {
                                    return (
                                        <tr
                                            key={index}
                                            className="border-b transition duration-300 ease-in-out hover:bg-slate-100 dark:border-slate-500 dark:hover:bg-slate-600"
                                        >
                                            <td className="whitespace-nowrap px-2 py-4 font-medium">
                                                {item.reference ?? ''} {JSON.parse(item?.request_information)?.claim_reference ? ` (${JSON.parse(item.request_information).claim_reference})` : ''}
                                            </td>
                                            <td className="whitespace-nowrap px-2 py-4 font-medium">
                                                {(() => {
                                                    try {
                                                        const requestInfo = typeof item.request_information === 'string'
                                                            ? JSON.parse(item.request_information)
                                                            : item.request_information
                                                        return requestInfo?.staff_details?.staff_name || ''
                                                    } catch (e) {
                                                        return ''
                                                    }
                                                })()}
                                            </td>
                                            <td className="whitespace-nowrap px-2 py-4 font-medium">
                                                {(() => {
                                                    try {
                                                        const requestInfo = typeof item.request_information === 'string'
                                                            ? JSON.parse(item.request_information)
                                                            : item.request_information
                                                        return requestInfo?.staff_details?.date_of_birth
                                                            ? moment(requestInfo.staff_details.date_of_birth).format('DD/MM/YYYY')
                                                            : 'N/A'
                                                    } catch (e) {
                                                        return 'N/A'
                                                    }
                                                })()}
                                            </td>
                                            <td className="whitespace-nowrap px-2 py-4 font-medium">
                                                <span
                                                    className={`${statusClass(
                                                        item.request_status
                                                    )} rounded-full px-2 py-1 capitalize font-medium`}
                                                >
                                                    {item.request_status}
                                                </span>
                                            </td>
                                            <td className="whitespace-nowrap px-2 py-4 font-medium">
                                                {item.description && item.description.length > 50
                                                    ? `${item.description.slice(0, 50)}...`
                                                    : item.description || ''}
                                            </td>
                                            {recordViewType === 'all' && (
                                                <td className="whitespace-nowrap px-2 py-4 font-medium capitalize">
                                                    {item.created_by_name}
                                                </td>
                                            )}
                                            <td className="whitespace-nowrap px-2 py-4 font-medium">
                                                {moment(item.created_at).format('DD/MM/YYYY')}
                                            </td>
                                            <td className="flex items-center font-medium mt-2">
                                                {(() => {
                                                    const status = (item?.request_status || '').toLowerCase()
                                                    const cancelStatusArray = [
                                                        'draft',
                                                        'due',
                                                        'awaiting consent',
                                                        'awaiting clinical review',
                                                        'clinical review completed',
                                                        'awaiting assessment',
                                                        'awaiting confirmation of appointment',
                                                        'appointment confirmed'
                                                    ];

                                                    const showCancel = cancelStatusArray.includes(status) && !item?.request_info_array?.extensions

                                                    return (
                                                        <>
                                                            <button
                                                                className="text-blue-500 hover:text-blue-600 hover:underline"
                                                                onClick={() => handleViewRequest(item)}
                                                            >
                                                                {(item?.request_status || '').toLowerCase() === 'draft' ? 'Edit' : 'View'}
                                                            </button>
                                                            {showCancel && (
                                                                <button
                                                                    className="text-rose-400 ml-4 hover:text-rose-600 hover:underline"
                                                                    onClick={() => handleCancelRequest(item)}
                                                                >
                                                                    Cancel
                                                                </button>
                                                            )}
                                                        </>
                                                    )
                                                })()}
                                            </td>
                                        </tr>
                                    )
                                })
                            ) : (
                                <tr>
                                    <td colSpan={recordViewType === 'all' ? 8 : 7} className="text-center py-8">
                                        <p className="no-records">No Requests found</p>
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                    <div className="flex items-center justify-between py-3 mt-2">
                        <div className="flex items-center space-x-4">
                            <div className="flex items-center space-x-2">
                                <span className="text-sm text-gray-700">Show</span>
                                <select
                                    value={itemsPerPage}
                                    onChange={(e) => {
                                        setItemsPerPage(Number(e.target.value))
                                        setCurrentPage(1)
                                    }}
                                    className="border border-gray-300 rounded-md px-3 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-teal-600"
                                >
                                    <option value={5}>5</option>
                                    <option value={10}>10</option>
                                    <option value={25}>25</option>
                                    <option value={50}>50</option>
                                    <option value={100}>100</option>
                                </select>
                                <span className="text-sm text-gray-700">entries</span>
                            </div>
                            <div>
                                <p className="text-sm text-gray-700">
                                    Showing
                                    <span className="font-medium"> {fromItem} </span>
                                    to
                                    <span className="font-medium"> {toItem} </span>
                                    of
                                    <span className="font-medium"> {totalItems} </span>
                                    results
                                </p>
                            </div>
                        </div>
                        <div>
                            <nav
                                className="isolate inline-flex -space-x-px rounded-md shadow-sm"
                                aria-label="Pagination"
                            >
                                <button
                                    onClick={previousPage}
                                    disabled={currentPage === 1}
                                    className="relative inline-flex items-center rounded-l-md border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-500 hover:bg-gray-50 focus:z-20 disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    <svg
                                        className="h-5 w-5"
                                        viewBox="0 0 20 20"
                                        fill="currentColor"
                                        aria-hidden="true"
                                    >
                                        <path
                                            fillRule="evenodd"
                                            d="M12.79 5.23a.75.75 0 01-.02 1.06L8.832 10l3.938 3.71a.75.75 0 11-1.04 1.08l-4.5-4.25a.75.75 0 010-1.08l4.5-4.25a.75.75 0 011.06.02z"
                                            clipRule="evenodd"
                                        />
                                    </svg>
                                    <span className="ml-1">Previous</span>
                                </button>


                                {(() => {
                                    const maxVisible = 5;
                                    let startPage = Math.max(1, currentPage - Math.floor(maxVisible / 2));
                                    let endPage = Math.min(totalPages, startPage + maxVisible - 1);

                                    if (endPage - startPage + 1 < maxVisible) {
                                        startPage = Math.max(1, endPage - maxVisible + 1);
                                    }

                                    const pages = [];

                                    if (startPage > 1) {
                                        pages.push(
                                            <button
                                                key={1}
                                                onClick={() => setCurrentPage(1)}
                                                className="relative inline-flex items-center border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-500 hover:bg-gray-50 focus:z-20"
                                            >
                                                1
                                            </button>
                                        );
                                        if (startPage > 2) {
                                            pages.push(
                                                <span key="dots1" className="relative inline-flex items-center border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700">
                                                    ...
                                                </span>
                                            );
                                        }
                                    }

                                    for (let i = startPage; i <= endPage; i++) {
                                        pages.push(
                                            <button
                                                key={i}
                                                onClick={() => setCurrentPage(i)}
                                                className={`relative inline-flex items-center border border-gray-300 px-4 py-2 text-sm font-medium focus:z-20 ${i === currentPage
                                                    ? 'bg-teal-600 text-white border-teal-600'
                                                    : 'bg-white text-gray-500 hover:bg-gray-50'
                                                    }`}
                                            >
                                                {i}
                                            </button>
                                        );
                                    }

                                    if (endPage < totalPages) {
                                        if (endPage < totalPages - 1) {
                                            pages.push(
                                                <span key="dots2" className="relative inline-flex items-center border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700">
                                                    ...
                                                </span>
                                            );
                                        }
                                        pages.push(
                                            <button
                                                key={totalPages}
                                                onClick={() => setCurrentPage(totalPages)}
                                                className="relative inline-flex items-center border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-500 hover:bg-gray-50 focus:z-20"
                                            >
                                                {totalPages}
                                            </button>
                                        );
                                    }

                                    return pages;
                                })()}

                                <button
                                    onClick={nextPage}
                                    disabled={currentPage === totalPages}
                                    className="relative inline-flex items-center rounded-r-md border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-500 hover:bg-gray-50 focus:z-20 disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    <span className="mr-1">Next</span>
                                    <svg
                                        className="h-5 w-5"
                                        viewBox="0 0 20 20"
                                        fill="currentColor"
                                        aria-hidden="true"
                                    >
                                        <path
                                            fillRule="evenodd"
                                            d="M7.21 14.77a.75.75 0 01.02-1.06L11.168 10 7.23 6.29a.75.75 0 111.04-1.08l4.5 4.25a.75.75 0 010 1.08l-4.5 4.25a.75.75 0 01-1.06-.02z"
                                            clipRule="evenodd"
                                        />
                                    </svg>
                                </button>
                            </nav>
                        </div>
                    </div>
                </div>
            </div>

            <SlideOut
                open={open}
                setOpen={setOpen}
                title="Viewing OH Request"
                subtitle="View details and status of this OH request."
                width='md'
            >
                <OHViewRequest
                    viewingRequest={selectedRequest}
                    onUpdate={() => { fetchRequests() }}
                    onClose={closeSlideOut}
                />
            </SlideOut>
            < CancelRequest
                isOpen={cancelModalOpen}
                onClose={closeModal}
                onCancel={cancelRequestClicked}
                viewingRequest={selectedRequest}
                title={(selectedRequest?.request_status || '').toLowerCase() === 'draft'
                    ? 'Cancel this draft referral?'
                    : 'Are you sure you want to cancel this referral?'
                }
                message={(selectedRequest?.request_status || '').toLowerCase() === 'draft'
                    ? 'Cancelling this draft will mark it as cancelled. You can no longer submit it unless you create a new referral.'
                    : `Cancelling this referral will invalidate the link that has been sent out to the employee. This action cannot be undone${!currentPolicyExpired ? ' and another referral will need to be made if necessary.' : '.'}`
                }
                confirmLabel={(selectedRequest?.request_status || '').toLowerCase() === 'draft'
                    ? 'Cancel Draft'
                    : 'Cancel Referral'
                }
                confirmButtonClassName={(selectedRequest?.request_status || '').toLowerCase() === 'draft'
                    ? 'inline-flex w-full justify-center rounded-md bg-rose-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-rose-700 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-rose-700 sm:col-start-2'
                    : undefined
                }
            />
        </div >
    )
}

export default OHDashboard